<template>
  <div
    v-if="!isLoginPage && !isWithdrawalClosed"
    class="bottom-floating-clock-container"
  >
    <div class="clock-flex-layout d-flex align-center gap-3 flex-row-reverse">
      <!-- Professional Standalone Analog Countdown Clock -->
      <div
        class="clock-avatar-wrapper cursor-pointer"
        :title="`Current Time: ${currentTimeFormatted} - Click to toggle deadline panel`"
        @click="toggleMinimize"
      >
        <svg
          viewBox="0 0 120 120"
          width="135"
          height="135"
          class="professional-clock-svg"
        >
          <defs>
            <!-- Outer 3D Rim Gradient with JCI GOLD #EDBE38 -->
            <linearGradient
              id="jciGoldRim"
              x1="0%"
              y1="0%"
              x2="100%"
              y2="100%"
            >
              <stop
                offset="0%"
                stop-color="#FFF3C4"
              />
              <stop
                offset="40%"
                stop-color="#EDBE38"
              />
              <stop
                offset="100%"
                stop-color="#B8860B"
              />
            </linearGradient>

            <!-- Inner Face Gradient with JCI NAVY #3A67B1 & Off Black #0A0F29 -->
            <radialGradient
              id="jciNavyFace"
              cx="35%"
              cy="35%"
              r="65%"
            >
              <stop
                offset="0%"
                stop-color="#3A67B1"
              />
              <stop
                offset="60%"
                stop-color="#1E3A70"
              />
              <stop
                offset="100%"
                stop-color="#0A0F29"
              />
            </radialGradient>

            <!-- 3D Glass Reflection -->
            <linearGradient
              id="jciGlass"
              x1="0%"
              y1="0%"
              x2="100%"
              y2="100%"
            >
              <stop
                offset="0%"
                stop-color="#FFFFFF"
                stop-opacity="0.25"
              />
              <stop
                offset="40%"
                stop-color="#FFFFFF"
                stop-opacity="0.05"
              />
              <stop
                offset="40.1%"
                stop-color="#FFFFFF"
                stop-opacity="0"
              />
              <stop
                offset="100%"
                stop-color="#FFFFFF"
                stop-opacity="0"
              />
            </linearGradient>

            <!-- Drop Shadow for Clock Hands -->
            <filter
              id="handShadow"
              x="-20%"
              y="-20%"
              width="140%"
              height="140%"
            >
              <feDropShadow
                dx="0.8"
                dy="1.5"
                stdDeviation="1"
                flood-color="#0A0F29"
                flood-opacity="0.6"
              />
            </filter>

            <!-- Drop Shadow for Clock Frame -->
            <filter
              id="clockShadow"
              x="-20%"
              y="-20%"
              width="140%"
              height="140%"
            >
              <feDropShadow
                dx="0"
                dy="5"
                stdDeviation="6"
                flood-color="#0A0F29"
                flood-opacity="0.45"
              />
            </filter>
          </defs>

          <g filter="url(#clockShadow)">
            <!-- Outer Gold Rim Ring -->
            <circle
              cx="60"
              cy="60"
              r="54"
              fill="url(#jciGoldRim)"
              stroke="#B8860B"
              stroke-width="1.5"
            />

            <!-- Inner Clock Face (JCI Navy) -->
            <circle
              cx="60"
              cy="60"
              r="48"
              fill="url(#jciNavyFace)"
              stroke="#0097D7"
              stroke-width="1"
            />

            <!-- Major Hour Ticks (12, 3, 6, 9) -->
            <g
              stroke="#EDBE38"
              stroke-width="2"
              stroke-linecap="round"
            >
              <line
                x1="60"
                y1="14"
                x2="60"
                y2="18"
              />
              <line
                x1="106"
                y1="60"
                x2="102"
                y2="60"
              />
              <line
                x1="60"
                y1="106"
                x2="60"
                y2="102"
              />
              <line
                x1="14"
                y1="60"
                x2="18"
                y2="60"
              />
            </g>

            <!-- Professional Clock Numbers 1-12 -->
            <g
              font-family="'Roboto', sans-serif"
              font-weight="800"
              font-size="7.5"
              fill="#FFFFFF"
              text-anchor="middle"
              dominant-baseline="central"
            >
              <text
                x="60"
                y="26"
                fill="#EDBE38"
              >12</text>
              <text
                x="77"
                y="30.5"
              >1</text>
              <text
                x="89.5"
                y="43"
              >2</text>
              <text
                x="94"
                y="60"
                fill="#EDBE38"
              >3</text>
              <text
                x="89.5"
                y="77"
              >4</text>
              <text
                x="77"
                y="89.5"
              >5</text>
              <text
                x="60"
                y="94"
                fill="#EDBE38"
              >6</text>
              <text
                x="43"
                y="89.5"
              >7</text>
              <text
                x="30.5"
                y="77"
              >8</text>
              <text
                x="26"
                y="60"
                fill="#EDBE38"
              >9</text>
              <text
                x="30.5"
                y="43"
              >10</text>
              <text
                x="43"
                y="30.5"
              >11</text>
            </g>

            <!-- Clock Hands Group -->
            <g filter="url(#handShadow)">
              <!-- Hour Hand (Gold) -->
              <line
                x1="60"
                y1="60"
                x2="60"
                y2="41"
                stroke="#EDBE38"
                stroke-width="3.5"
                stroke-linecap="round"
                :transform="`rotate(${hourAngle}, 60, 60)`"
              />

              <!-- Minute Hand (Aqua Blue) -->
              <line
                x1="60"
                y1="60"
                x2="60"
                y2="30"
                stroke="#0097D7"
                stroke-width="2.5"
                stroke-linecap="round"
                :transform="`rotate(${minuteAngle}, 60, 60)`"
              />

              <!-- Second Hand (Seafoam) -->
              <line
                x1="60"
                y1="68"
                x2="60"
                y2="23"
                stroke="#56BDA3"
                stroke-width="1.5"
                stroke-linecap="round"
                :transform="`rotate(${secondAngle}, 60, 60)`"
              />

              <!-- Center Cap Dot -->
              <circle
                cx="60"
                cy="60"
                r="3.5"
                fill="#56BDA3"
                stroke="#FFFFFF"
                stroke-width="1"
              />
            </g>

            <!-- 3D Glass Shine Layer -->
            <circle
              cx="60"
              cy="60"
              r="48"
              fill="url(#jciGlass)"
            />
          </g>
        </svg>
      </div>

      <!-- Professional Deadline Panel Card (Expanded View) -->
      <div
        v-if="!isMinimized"
        class="withdrawal-deadline-card pa-3 rounded-lg elevation-12"
      >
        <div
          class="d-flex align-center justify-space-between mb-3 px-1 border-bottom pb-2"
        >
          <div class="d-flex align-center">
            <v-icon
              color="#EDBE38"
              size="18"
              class="mr-2"
            >
              mdi-clock-alert-outline
            </v-icon>
            <span class="timeline-header-title">WITHDRAWAL DEADLINE</span>
          </div>
          <v-btn
            icon
            density="compact"
            variant="text"
            size="small"
            color="#94A3B8"
            class="pa-0 ml-2 minimize-btn"
            title="Minimize"
            @click="toggleMinimize"
          >
            <v-icon size="16">
              mdi-close
            </v-icon>
          </v-btn>
        </div>

        <!-- Section 1: Withdrawal Deadline -->
        <div class="panel-section mb-2 px-3 py-2 rounded">
          <div class="section-label mb-1">
            WITHDRAWAL DEADLINE
          </div>
          <div class="deadline-value">
            {{ withdrawalDeadlineText }}
          </div>
        </div>

        <!-- Section 2: Time Remaining (Visual Priority) -->
        <div class="panel-section px-3 py-2 rounded priority-section">
          <div class="section-label mb-1 priority-label">
            TIME REMAINING
          </div>
          <div class="remaining-value">
            {{ remainingTimeText }}
          </div>
        </div>
      </div>

      <!-- Professional Minimized State -->
      <div
        v-else
        class="mini-countdown-badge d-flex align-center px-3 py-2 rounded-pill elevation-6 cursor-pointer"
        title="Click to expand Withdrawal Deadline panel"
        @click="toggleMinimize"
      >
        <v-icon
          color="#EDBE38"
          size="16"
          class="mr-2"
        >
          mdi-timer-sand
        </v-icon>
        <div class="mini-countdown-content d-flex align-center">
          <span class="mini-countdown-label mr-1.5">Time Remaining:</span>
          <span class="mini-countdown-value">{{ remainingTimeText }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import companyConfig from "@/company_config.json";

export default {
  name: "RealTimeClockMascot",
  props: {
    withdrawalDateTime: {
      type: [String, Date],
      default: null,
    },
    conference: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      fetchedConference: {},
      now: new Date(),
      timerInterval: null,
      fetchInterval: null,
      isMinimized: false,
    };
  },
  computed: {
    isLoginPage() {
      if (!this.$route) return false;
      const path = this.$route.path ? this.$route.path.toLowerCase() : "";
      const name = this.$route.name
        ? String(this.$route.name).toLowerCase()
        : "";
      return path === "/login" || path === "/" || name === "login";
    },
    isWithdrawalClosed() {
      const target = this.parseDateTime(this.targetDateStr);
      if (!target) return false;
      return target.getTime() - this.now.getTime() <= 0;
    },
    activeConference() {
      if (
        this.conference &&
        (this.conference.WithdrawalDateTime ||
          this.conference.WithdrawalEndDate)
      ) {
        return this.conference;
      }
      return this.fetchedConference;
    },
    targetDateStr() {
      if (this.withdrawalDateTime) {
        return this.withdrawalDateTime;
      }
      const conf = this.activeConference;
      if (conf) {
        return (
          conf.WithdrawalDateTime ||
          conf.WithdrawalEndDate ||
          conf.conference_withdrawal_end_date ||
          ""
        );
      }
      return "";
    },
    withdrawalDeadlineText() {
      const conf = this.activeConference;
      if (conf && conf.WithdrawalEndDate) {
        return conf.WithdrawalEndDate;
      }
      if (typeof this.withdrawalDateTime === "string") {
        return this.withdrawalDateTime;
      }
      return "Not Specified";
    },
    remainingTimeText() {
      const target = this.parseDateTime(this.targetDateStr);
      if (!target) return "Not Specified";

      const diff = target.getTime() - this.now.getTime();
      if (diff <= 0) {
        return "Withdrawal Closed";
      }

      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor(
        (diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
      );
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);

      const pad = (num) => String(num).padStart(2, "0");
      if (days > 0) {
        return `${days}d ${pad(hours)}h ${pad(minutes)}m ${pad(seconds)}s`;
      }
      return `${pad(hours)}h ${pad(minutes)}m ${pad(seconds)}s`;
    },
    hourAngle() {
      const hours = this.now.getHours() % 12;
      const minutes = this.now.getMinutes();
      const seconds = this.now.getSeconds();
      return (hours + minutes / 60 + seconds / 3600) * 30;
    },
    minuteAngle() {
      const minutes = this.now.getMinutes();
      const seconds = this.now.getSeconds();
      return (minutes + seconds / 60) * 6;
    },
    secondAngle() {
      const seconds = this.now.getSeconds();
      const ms = this.now.getMilliseconds();
      return (seconds + ms / 1000) * 6;
    },
    currentTimeFormatted() {
      return this.now.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: true,
      });
    },
  },
  mounted() {
    this.fetchConferenceDetails();
    this.timerInterval = setInterval(() => {
      this.now = new Date();
    }, 100);

    this.fetchInterval = setInterval(() => {
      this.fetchConferenceDetails();
    }, 60000);
  },
  beforeUnmount() {
    if (this.timerInterval) clearInterval(this.timerInterval);
    if (this.fetchInterval) clearInterval(this.fetchInterval);
  },
  methods: {
    fetchConferenceDetails() {
      if (
        this.withdrawalDateTime ||
        (this.conference &&
          (this.conference.WithdrawalDateTime ||
            this.conference.WithdrawalEndDate))
      ) {
        return;
      }
      try {
        var token = this.$session ? this.$session.get("token") : null;
        if (!token) return;
        var server_url = companyConfig.apiURL;
        var add_url = server_url + "api/nomination-conference/show";
        var upload = {
          UserInterface: 2,
        };
        const thisIns = this;
        this.$http({
          url: add_url,
          method: "POST",
          data: upload,
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
        })
          .then(function (response) {
            if (
              response.data &&
              response.data.flag == 1 &&
              response.data.records
            ) {
              thisIns.fetchedConference = response.data.records;
            }
          })
          .catch(function (error) {
            console.log("RealTimeClockMascot fetch error:", error);
          });
      } catch (e) {
        console.log("RealTimeClockMascot error:", e);
      }
    },
    parseDateTime(dateStr) {
      if (!dateStr || typeof dateStr !== "string") return null;
      const str = dateStr.trim();
      if (
        !str ||
        str === "null" ||
        str === "undefined" ||
        str.startsWith("0000")
      ) {
        return null;
      }

      if (/^\d{4}-\d{2}-\d{2}\s\d{2}:\d{2}/.test(str)) {
        const iso = str.replace(" ", "T");
        const parsed = new Date(iso);
        if (!isNaN(parsed.getTime())) return parsed;
      }

      const parsed = new Date(str);
      if (!isNaN(parsed.getTime())) return parsed;

      return null;
    },
    toggleMinimize() {
      this.isMinimized = !this.isMinimized;
    },
  },
};
</script>

<style scoped>
/* Container at Bottom Right */
.bottom-floating-clock-container {
  position: fixed;
  bottom: 24px;
  right: 24px;
  z-index: 9995;
  font-family: "Roboto", sans-serif;
  user-select: none;
}

.clock-flex-layout {
  display: flex;
  align-items: center;
  gap: 14px;
  flex-direction: row-reverse;
}

.clock-avatar-wrapper {
  transition: transform 0.2s ease;
  flex-shrink: 0;
}

.clock-avatar-wrapper:hover {
  transform: scale(1.04);
}

.professional-clock-svg {
  display: block;
  overflow: visible;
}

/* Professional Enterprise Deadline Panel */
.withdrawal-deadline-card {
  background: linear-gradient(135deg, #0a0f29 0%, #1e3a70 100%);
  border: 1px solid #3a67b1;
  box-shadow: 0 10px 30px rgba(10, 15, 41, 0.6), 0 0 15px rgba(58, 103, 177, 0.2);
  backdrop-filter: blur(12px);
  width: 320px;
  max-width: 90vw;
  color: #ffffff;
}

.timeline-header-title {
  font-size: 0.72rem;
  font-weight: 800;
  letter-spacing: 0.8px;
  color: #edbe38; /* JCI GOLD */
  text-transform: uppercase;
}

.minimize-btn {
  min-width: 20px !important;
  height: 20px !important;
}

.border-bottom {
  border-bottom: 1px solid rgba(255, 255, 255, 0.1) !important;
}

.panel-section {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.08);
}

.section-label {
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.5px;
  color: #94a3b8;
  text-transform: uppercase;
}

.deadline-value {
  font-family: monospace, monospace;
  font-size: 0.88rem;
  font-weight: 700;
  color: #56bda3; /* JCI SEAFOAM */
  word-break: break-word;
}

.priority-section {
  background: rgba(237, 190, 56, 0.08);
  border: 1px solid rgba(237, 190, 56, 0.25);
}

.priority-label {
  color: #edbe38;
}

.remaining-value {
  font-family: monospace, monospace;
  font-size: 1.05rem;
  font-weight: 800;
  color: #edbe38; /* JCI GOLD */
  letter-spacing: 0.5px;
}

/* Minimized State Badge */
.mini-countdown-badge {
  background: linear-gradient(135deg, #0a0f29 0%, #1e3a70 100%);
  border: 1px solid #edbe38;
  box-shadow: 0 6px 18px rgba(10, 15, 41, 0.5);
  backdrop-filter: blur(10px);
  color: #ffffff;
  transition: transform 0.2s ease, border-color 0.2s ease;
  white-space: nowrap;
}

.mini-countdown-badge:hover {
  transform: translateY(-1px);
  border-color: #ffffff;
}

.mini-countdown-label {
  font-size: 0.72rem;
  font-weight: 700;
  color: #cbd5e1;
}

.mini-countdown-value {
  font-family: monospace, monospace;
  font-size: 0.88rem;
  font-weight: 800;
  color: #edbe38; /* JCI GOLD */
}

/* Responsive Layout */
@media (max-width: 640px) {
  .bottom-floating-clock-container {
    bottom: 16px;
    right: 16px;
  }
  .clock-flex-layout {
    flex-direction: column;
    align-items: flex-end;
    gap: 8px;
  }
  .professional-clock-svg {
    width: 110px !important;
    height: 110px !important;
  }
  .withdrawal-deadline-card {
    width: 280px;
  }
}
</style>
